/**
 * PartnerEarningsCard — Full-width gradient hero card for the affiliate dashboard.
 *
 * Gradient: #C4156E → #8B5CF6 (darkened pink for WCAG AA white text contrast).
 * White text on #C4156E = 7.3:1 (AAA). Safe for all text sizes.
 *
 * Layout:
 *   "This Month" label + CommissionPill (top row)
 *   $127.50 hero value (large)
 *   Divider
 *   Total | Pending | Commission (3-col sub-metrics)
 */

import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { createBoxShadow } from '../../../lib/design-tokens';

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const GRADIENT_COLORS = ['#C4156E', '#8B5CF6'] as const;
const GRADIENT_START = { x: 0, y: 0 };
const GRADIENT_END = { x: 1, y: 1 };

const CARD_SHADOW = createBoxShadow({
  color: '#8B5CF6',
  offsetX: 0,
  offsetY: 8,
  blurRadius: 24,
  opacity: 0.30,
  elevation: 8,
});

const PILL_BACKGROUND = 'rgba(255,255,255,0.22)';
const PILL_BORDER_COLOR = 'rgba(255,255,255,0.40)';
const DIVIDER_COLOR = 'rgba(255,255,255,0.30)';
const SUBLABEL_COLOR = 'rgba(255,255,255,0.75)';

// ---------------------------------------------------------------------------
// Sub-metric column
// ---------------------------------------------------------------------------

interface EarningsSubMetricProps {
  value: string;
  label: string;
  accessibilityLabel: string;
}

const EarningsSubMetric: React.FC<EarningsSubMetricProps> = ({
  value,
  label,
  accessibilityLabel,
}) => (
  <View
    style={styles.subMetric}
    accessible={true}
    accessibilityLabel={accessibilityLabel}
  >
    <Text style={styles.subMetricValue}>{value}</Text>
    <Text style={styles.subMetricLabel}>{label}</Text>
  </View>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface PartnerEarningsCardProps {
  /** Current month earnings, e.g. 127.50 */
  thisMonthAmount: number;
  /** Lifetime total earned */
  totalAmount: number;
  /** Amount pending payout */
  pendingAmount: number;
  /** Commission rate as a decimal, e.g. 0.25 */
  commissionRate: number;
  /** Locale for currency formatting. Default: 'en-US' */
  locale?: string;
  /** Currency code. Default: 'USD' */
  currency?: string;
  /** i18n labels */
  thisMonthLabel: string;
  totalLabel: string;
  pendingLabel: string;
  commissionLabel: string;
  commissionPillLabel: string;
  thisMonthAccessibilityLabel: string;
  totalAccessibilityLabel: string;
  pendingAccessibilityLabel: string;
  commissionAccessibilityLabel: string;
}

export const PartnerEarningsCard: React.FC<PartnerEarningsCardProps> = React.memo(
function PartnerEarningsCard({
  thisMonthAmount,
  totalAmount,
  pendingAmount,
  commissionRate,
  locale = 'en-US',
  currency = 'USD',
  thisMonthLabel,
  totalLabel,
  pendingLabel,
  commissionLabel,
  commissionPillLabel,
  thisMonthAccessibilityLabel,
  totalAccessibilityLabel,
  pendingAccessibilityLabel,
  commissionAccessibilityLabel,
}) {
  const formatter = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'currency', currency }),
    [locale, currency]
  );

  const formatCurrency = useCallback(
    (amount: number) => formatter.format(amount),
    [formatter]
  );

  const formatPercent = (rate: number) =>
    `${Math.round(rate * 100)}%`;

  const heroValue = formatCurrency(thisMonthAmount);
  const totalValue = formatCurrency(totalAmount);
  const pendingValue = formatCurrency(pendingAmount);
  const commissionValue = formatPercent(commissionRate);

  return (
    <LinearGradient
      colors={GRADIENT_COLORS}
      start={GRADIENT_START}
      end={GRADIENT_END}
      style={[styles.card, CARD_SHADOW as ViewStyle]}
    >
      {/* Top row: label + commission pill */}
      <View style={styles.topRow}>
        <Text style={styles.thisMonthLabel}>{thisMonthLabel}</Text>
        <View
          style={styles.commissionPill}
          accessible={true}
          accessibilityLabel={commissionPillLabel}
        >
          <Text style={styles.commissionPillText}>{commissionPillLabel}</Text>
        </View>
      </View>

      {/* Hero earnings value */}
      <Text
        style={styles.heroValue}
        accessibilityLabel={thisMonthAccessibilityLabel}
      >
        {heroValue}
      </Text>

      {/* Divider */}
      <View style={styles.divider} />

      {/* 3-column sub-metrics */}
      <View style={styles.subMetricsRow}>
        <EarningsSubMetric
          value={totalValue}
          label={totalLabel}
          accessibilityLabel={totalAccessibilityLabel}
        />
        <View style={styles.verticalDivider} />
        <EarningsSubMetric
          value={pendingValue}
          label={pendingLabel}
          accessibilityLabel={pendingAccessibilityLabel}
        />
        <View style={styles.verticalDivider} />
        <EarningsSubMetric
          value={commissionValue}
          label={commissionLabel}
          accessibilityLabel={commissionAccessibilityLabel}
        />
      </View>
    </LinearGradient>
  );
});

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  card: {
    borderRadius: 20,
    marginHorizontal: 16,
    padding: 20,
  } as ViewStyle,
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  } as ViewStyle,
  thisMonthLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
  } as TextStyle,
  commissionPill: {
    backgroundColor: PILL_BACKGROUND,
    borderWidth: 1,
    borderColor: PILL_BORDER_COLOR,
    borderRadius: 9999,
    paddingVertical: 4,
    paddingHorizontal: 10,
  } as ViewStyle,
  commissionPillText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#7C3AED',
  } as TextStyle,
  heroValue: {
    fontSize: 40,
    fontWeight: '800',
    color: '#FFFFFF',
    letterSpacing: -1.2,
    marginBottom: 16,
  } as TextStyle,
  divider: {
    height: 1,
    backgroundColor: DIVIDER_COLOR,
    marginBottom: 16,
  } as ViewStyle,
  subMetricsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  } as ViewStyle,
  subMetric: {
    flex: 1,
    alignItems: 'center',
  } as ViewStyle,
  subMetricValue: {
    fontSize: 15,
    fontWeight: '800',
    color: '#FFFFFF',
    marginBottom: 2,
  } as TextStyle,
  subMetricLabel: {
    fontSize: 11,
    fontWeight: '400',
    color: SUBLABEL_COLOR,
  } as TextStyle,
  verticalDivider: {
    width: 1,
    height: '100%',
    backgroundColor: DIVIDER_COLOR,
  } as ViewStyle,
});
